import React, { useState, useContext, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery, useMutation } from "react-apollo";
import { Tag, Title, Tab, Field, Control, Button, Icon } from "rbx";
import {
  Link,
  Route,
  useParams,
  useHistory,
  useLocation,
  Switch
} from "react-router-dom";

import Loading from "../../../components/Loading";

import { AddCustomerContactForm } from "../../../components/CustomerContacts";
import AddCustomerNoteForm from "../../../components/Customers/AddCustomerNoteForm";

import { ModalContext } from "../../../components/ModalProvider";
import { getUTCFormattedDate, ourToast as toast } from "../../../utils";

import {
  SINGLE_CUSTOMER_QUERY,
  UPDATE_CUSTOMER_MUTATION,
  DELETE_CUSTOMER_MUTATION
} from "../../../graphql/Customers/Customers.graphql";

import Details from "./routes/Details";
import Contacts from "./routes/Contacts";
import Notes from "./routes/Notes";

import IconLink from "../../../components/IconLink";
import { DeleteButton } from "../../../components/inputs";
import { useSystemCodes } from "../../../utils/hooks";
import { useAllowCustomerNoChange } from "../../../hooks";

const INITIAL_STATE = {
  CustomerNo: "",
  Name: "",
  Active: "",
  BillToCust: "",
  OrderCust: "",
  CustomerTypeRecIdRef: "",
  Telephone: "",
  FaxNo: "",
  BillingContactName: "",
  LastSaleDate: "",
  LastSaleAmount: "",
  Address1: "",
  Address2: "",
  City: "",
  State: "",
  Zip: "",
  PaymentTermsRecIdRef: "",
  ELDStatusRecIdRef: "",
  ELDStatusChange: false,
  RevGLRecIdRef: "",
  ArGLRecIdRef: ""
};

const CustomerPage = () => {
  const { data: systemCodes } = useSystemCodes({
    Category: "General Ledger",
    CodeName: "AcctType"
  });
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();

  const { data } = useQuery(SINGLE_CUSTOMER_QUERY, {
    variables: { where: { RecId: parseInt(id) } }
  });
  const [updateCustomer, { loading }] = useMutation(UPDATE_CUSTOMER_MUTATION, {
    refetchQueries: ["ALL_CUSTOMERS_QUERY", "SINGLE_CUSTOMER_QUERY"]
  });

  const [editing, setEditing] = useState(true);
  const [inputs, setInputs] = useState({ ...INITIAL_STATE });
  const [codes, setCodes] = useState(null);
  const { setModalOpen } = useContext(ModalContext);

  useEffect(() => {
    if (data?.Customer) {
      setInputs(data.Customer);
    }
  }, [data]);

  const isAllowedDeleteCustomer = useAllowCustomerNoChange(parseInt(id, 10));

  useEffect(() => {
    if (!codes && Array.isArray(systemCodes)) {
      setCodes(systemCodes);
    }
  }, [codes, systemCodes]);

  const toggleEditing = () => setEditing(!editing);

  const handleChange = e => {
    const updates = { ...inputs };
    if (e.target.type === "checkbox") {
      updates[e.target.name] = e.target.checked;
    } else {
      updates[e.target.name] = e.target.name.includes("PaymentTermsRecIdRef")
        ? parseInt(e.target.value)
        : e.target.value;
    }
    updates.ELDStatusChange = true;
    setInputs({ ...updates });
  };
  const handleSave = async e => {
    e.preventDefault();
    try {
      const obj = Object.keys(INITIAL_STATE).reduce(
        (acc, curr) =>
          inputs[curr] !== null ? { ...acc, [curr]: inputs[curr] } : acc,
        {}
      );
      await updateCustomer({
        variables: {
          data: {
            ...obj,
            LastSaleAmount: obj.LastSaleAmount
              ? parseFloat(obj.LastSaleAmount)
              : null,
            LastSaleDate: obj.LastSaleDate
              ? getUTCFormattedDate(obj.LastSaleDate)
              : null,
            CustomerTypeRecIdRef: parseInt(obj.CustomerTypeRecIdRef),
            PaymentTermsRecIdRef:
              obj.PaymentTermsRecIdRef && obj.BillToCust
                ? obj.PaymentTermsRecIdRef
                : null,
            ELDStatusRecIdRef:
              inputs.ELDStatusRecIdRef && inputs.BillToCust
                ? parseInt(inputs.ELDStatusRecIdRef)
                : null,
            RevGLRecIdRef: inputs.RevGLRecIdRef
              ? parseInt(inputs.RevGLRecIdRef)
              : null,
            ArGLRecIdRef: inputs.ArGLRecIdRef
              ? parseInt(inputs.ArGLRecIdRef)
              : null
          },
          where: { RecId: parseInt(id) }
        }
      });
      setEditing(false);
      toast.success("Customer updated successfully.");
    } catch (err) {
      toast.error("Error updating Customer.");
    }
  };

  if (loading || !data?.Customer || !codes) return <Loading />;
  const getActionButtons = () => {
    if (location.pathname.includes("details")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="primary"
              disabled={editing}
              size="small"
              onClick={toggleEditing}
            >
              <Icon>
                <FontAwesomeIcon icon="edit" />
              </Icon>
              <span>Edit</span>
            </Button>
          </Control>
          <Control>
            <DeleteButton
              mutation={DELETE_CUSTOMER_MUTATION}
              refetchQueries={["ALL_CUSTOMERS_QUERY", "SINGLE_CUSTOMER_QUERY"]}
              size="small"
              variables={{ where: { RecId: parseInt(id) } }}
              warningMessage={
                !isAllowedDeleteCustomer
                  ? "This customer cannot be deleted as it has already been used."
                  : ""
              }
              onComplete={() => history.push("/dispatch/customers")}
            />
          </Control>
          <Control>
            <Button
              color="success"
              disabled={!editing}
              size="small"
              onClick={handleSave}
            >
              <Icon>
                <FontAwesomeIcon icon="save" />
              </Icon>
              <span>Save</span>
            </Button>
          </Control>
        </Field>
      );
    }
    if (location.pathname.includes("notes")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="success"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <AddCustomerNoteForm
                    id={id}
                    onComplete={() => setModalOpen(false, "")}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="plus" />
              </Icon>
              <span>Add Note</span>
            </Button>
          </Control>
        </Field>
      );
    }
    return (
      <Field kind="group">
        <Control>
          <Button
            color="success"
            size="small"
            onClick={() =>
              setModalOpen(
                true,
                <AddCustomerContactForm
                  CustomerRecIdRef={inputs.RecId}
                  setModalOpen={setModalOpen}
                />
              )
            }
          >
            <Icon>
              <FontAwesomeIcon icon="plus" />
            </Icon>
            <span>Add</span>
          </Button>
        </Control>
      </Field>
    );
  };
  return (
    <React.Fragment>
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={1}>
            {inputs?.Name}
          </Title>
          <div className="page-head-start--subheader">
            <IconLink
              href={`tel:${inputs?.Telephone}`}
              icon="phone-alt"
              label={inputs?.Telephone}
            />
          </div>
          <Tag.Group gapless>
            <Tag color={inputs?.Active ? "success" : "danger"}>
              {inputs?.Active ? "ACTIVE" : "INACTIVE"}
            </Tag>
            {data?.Customer?.BillToCust && data?.Customer?.ELDStatus && (
              <Tag color="dark">{data.Customer.ELDStatus.CodeDescription}</Tag>
            )}
          </Tag.Group>
        </div>
        <div className="page-head-end">{getActionButtons(location)}</div>
      </header>
      <Tab.Group kind="boxed">
        <Tab
          active={location.pathname.includes("details")}
          as={Link}
          tabIndex="-1"
          to="details"
        >
          <span>Details</span>
        </Tab>
        <Tab
          active={location.pathname.includes("contacts")}
          as={Link}
          tabIndex="-1"
          to="contacts"
        >
          <span>Contacts</span>
        </Tab>
        <Tab
          active={location.pathname.includes("notes")}
          as={Link}
          tabIndex="-1"
          to="notes"
        >
          <span>Notes</span>
        </Tab>
      </Tab.Group>
      <Switch>
        <Route path="/finance/customers/:id/details">
          <Details
            assetCode={codes?.find(c => c.CodeId === "A")}
            customer={inputs}
            editing={editing}
            revenueCode={codes?.find(c => c.CodeId === "R")}
            onChange={handleChange}
          />
        </Route>
        <Route path="/finance/customers/:id/contacts">
          <Contacts id={inputs?.RecId} />
        </Route>
        <Route path="/finance/customers/:id/notes">
          <Notes id={id} />
        </Route>
      </Switch>
    </React.Fragment>
  );
};

CustomerPage.propTypes = {};

export default CustomerPage;
